import { useEffect, useRef } from "react";

import { useActiveStream } from "@/anticheat/react/useActiveStream";

/**
 * Small mirrored thumbnail of the candidate's camera, anchored bottom-right
 * of the viewport. Reuses the MediaStream singleton owned by anticheat/camera
 * — never opens its own stream. Renders null when no stream is active (e.g.
 * before Gate, after Done, or in Practice before the candidate opts in).
 *
 * The outer wrapper is `pointer-events-none` so the underlying Monaco editor
 * and MCQ option buttons stay clickable. No interactive children in v1, so
 * no `pointer-events-auto` escape hatch is needed.
 *
 * Positioned inside `document.documentElement` (the fullscreen root), so the
 * preview stays visible when Question.tsx is fullscreen.
 *
 * z-40 keeps the preview above the question body but below the
 * FullscreenBlockingOverlay (z-50) — the overlay intentionally covers the
 * preview while the candidate is out of fullscreen, since the
 * "your camera is alive" reassurance is moot in that moment.
 */
interface CameraPreviewProps {
  // EF-8: the live reminder the candidate sees while snapshots run ("A photo
  // is taken every 30 s"). Rendered OUTSIDE the aria-hidden mirror so screen
  // readers get it too; undefined = no caption (Practice, the Gate, a test
  // without snapshots).
  caption?: string;
}

export function CameraPreview({ caption }: CameraPreviewProps = {}) {
  const stream = useActiveStream();
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !stream) return;
    // This preview is purely decorative (aria-hidden, pointer-events-none). A
    // failure attaching or playing the stream must NEVER tear down the
    // in-progress assessment: without this guard a thrown `srcObject` setter
    // (seen when a stream ends / a device is revoked mid-test) bubbles to the
    // route ErrorBoundary and replaces the whole question view (qa-2). Swallow
    // it — the candidate's answers and timer are unaffected by a missing mirror.
    try {
      // Stream identity check: re-attaching the same MediaStream resets playback
      // in some Chromium builds. Only attach when the reference differs.
      if (video.srcObject !== stream) {
        video.srcObject = stream;
      }
      // .play() can reject even on muted video in some browsers; the existing
      // detector wraps the same way at camera.ts:202-207.
      void video.play().catch(() => {});
    } catch {
      /* decorative preview — a stream-attach failure must not surface */
    }
    return () => {
      try {
        // Clear srcObject so the HTMLMediaElement doesn't retain a reference
        // to the stream after unmount. Same cleanup pattern as
        // createCameraMonitor.cleanupVideo (camera.ts:132-139).
        video.srcObject = null;
      } catch {
        /* decorative cleanup — ignore */
      }
    };
  }, [stream]);

  if (!stream) return null;

  return (
    <div className="pointer-events-none fixed bottom-4 right-4 z-40 flex flex-col items-end gap-1">
      {caption && (
        <p
          role="status"
          className="max-w-[160px] rounded bg-background/90 px-2 py-0.5 text-right text-3xs leading-snug text-muted-foreground shadow"
        >
          {caption}
        </p>
      )}
    <div
      aria-hidden="true"
    >
      <video
        ref={videoRef}
        autoPlay
        muted
        playsInline
        // B10: 160×120 is a third of a 375px-wide phone screen and sat on top
        // of the cookie strip's "Got it". Smaller below `sm`; unchanged from
        // 640px up, which is every viewport this was designed against.
        className="h-[84px] w-[112px] rounded-md border border-border bg-black object-cover shadow-lg sm:h-[120px] sm:w-[160px]"
        // Mirror: matches Zoom / Meet / Teams / FaceTime self-view convention.
        // An unmirrored preview reads as wrong even though it's technically truthful.
        style={{ transform: "scaleX(-1)" }}
      />
    </div>
    </div>
  );
}
